import type {Router, RouteLocationNormalizedLoaded, RouteLocationRaw} from 'vue-router'

/**
 * Structural page hierarchy powering the header back button.
 *
 * Kept out of route `meta` on purpose: vue-router merges meta down `route.matched`,
 * so a `parent` on a layout record (Organization, Project, Feature, ProjectSettings)
 * would silently leak to every descendant that did not override it.
 *
 * A parent may be a function of the current route for edges that depend on data
 * rather than routing (e.g. a sub-project's parent project).
 */
export type RouteParent = string | ((route: RouteLocationNormalizedLoaded) => RouteLocationRaw | null)

/** Pages at the top of the hierarchy: no back button. */
export const ROUTE_ROOTS = new Set([
  'OrgProjects',
  'OrgLayers',
  'OrgChats',
  'OrgReportsPage',
  'OrgPayments',
  'OrgKyc',
  'Dashboard',
  'SignIn',
  'SignUp',
  'AuthOverview',
  'Invite',
  'MapInvite',
  'NoService',
  'Logout',
])

export const ROUTE_PARENT: Record<string, RouteParent> = {
  FeatureEdit: 'FeatureIndex',
  FeatureIndex: 'ProjectHome',

  // Back from a Place goes up one level; PlaceBrowser redirects further up past skipped Places.
  OrgProjectPlaces: (route) => {
    const place = ([] as string[]).concat(route.params.place || []).slice(0, -1)
    return place.length ? {name: 'OrgProjectPlaces', params: {place}} : {name: 'OrgProjects'}
  },

  ProjectHome: 'OrgProjects',
  ProjectMonitoring: 'OrgProjects',
  ProjectAlerts: 'OrgProjects',
  ProjectImagery: 'OrgProjects',
  ProjectChat: 'OrgProjects',
  CreateProject: 'OrgProjects',

  // /projects/:project_id/create is a sub-project of the project you are in.
  ProjectCreate: 'ProjectHome',
  ProjectEdit: 'ProjectHome',
  ProjectMembers: 'ProjectHome',
  ProjectTemplates: 'ProjectHome',
  ProjectMonitoringSettings: 'ProjectHome',
  ProjectSkills: 'ProjectHome',
  ProjectChatSettings: 'ProjectHome',
  ProjectAgentsSettings: 'ProjectHome',
  ProjectImport: 'ProjectHome',

  CreateTemplate: 'ProjectTemplates',
  EditTemplate: 'ProjectTemplates',

  LayerCreate: 'OrgLayers',
  LayerDetail: 'OrgLayers',
  MapCreate: 'OrgLayers',
  MapEdit: 'OrgLayers',
  MapMembers: 'MapEdit',

  ReportDetail: 'OrgReportsPage',

  OrgMembers: 'OrgProjects',
  OrgSettings: 'OrgProjects',
  OrgApiKeys: 'OrgProjects',
  OrgBilling: 'OrgProjects',
  User: 'OrgProjects',
  FileViewer: 'OrgProjects',

  Admin: 'OrgProjects',
  AdminUsers: 'Admin',
  AdminOrganizations: 'Admin',
  AdminTemplates: 'Admin',
  AdminWorkflows: 'Admin',
  AdminSkills: 'Admin',
  AdminAgents: 'Admin',
  AdminPrompts: 'Admin',
  AdminPlanet: 'Admin',
  AdminCreateTemplate: 'AdminTemplates',
  AdminEditTemplate: 'AdminTemplates',
}

/** Anything not listed above still needs a way out: the org's project list. */
export const FALLBACK_PARENT = 'OrgProjects'

/**
 * A typo'd route name here only fails at runtime, deep inside the matcher, so
 * surface it at boot in dev instead.
 */
export const validateHierarchy = (router: Router) => {
  const known = new Set(router.getRoutes().map(r => r.name).filter(Boolean))
  const unknown = [
    ...ROUTE_ROOTS,
    ...Object.keys(ROUTE_PARENT),
    ...Object.values(ROUTE_PARENT).filter(p => typeof p === 'string'),
    FALLBACK_PARENT,
  ].filter(name => !known.has(name as string))

  if (unknown.length) {
    console.error('[hierarchy] unknown route name(s):', [...new Set(unknown)].join(', '))
  }
}
